import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ArrowSquareOutIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  MicrophoneIcon,
  MonitorIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  checkRecordingPermissions,
  getRecordingSupport,
  getSystemAudioStatus,
  openRecordingPrivacySettings,
  requestMicPermission,
  requestScreenRecordingPermission,
  requestSystemAudioPermission,
  type RecordingPermissions,
} from '@/lib/utils/tauri-recording'
import { cn } from '@/lib/utils'

interface RecordingPermissionsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onGranted?: () => void
}

function PermissionRow({
  icon: Icon,
  title,
  description,
  status,
  onRequest,
  onOpenSettings,
  requestLabel,
}: {
  icon: typeof MicrophoneIcon
  title: string
  description: string
  status: string
  onRequest: () => void
  onOpenSettings: () => void
  requestLabel?: string
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border p-3">
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {status === 'granted' ? (
        <CheckCircleIcon
          className="mt-1 h-5 w-5 shrink-0 text-emerald-500"
          weight="fill"
        />
      ) : status === 'pending' ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CircleNotchIcon className="h-4 w-4 animate-spin" />
          Waiting…
        </div>
      ) : status === 'denied' ? (
        <Button size="sm" variant="outline" onClick={onOpenSettings}>
          <ArrowSquareOutIcon className="mr-1.5 h-3.5 w-3.5" />
          Open Settings
        </Button>
      ) : (
        <Button size="sm" onClick={onRequest}>
          {requestLabel ?? 'Allow'}
        </Button>
      )}
    </div>
  )
}

/**
 * Walks the user through macOS TCC permissions for meeting recording.
 * Note the screen-recording quirk: after granting in System Settings the
 * app must be restarted before macOS reports it as granted.
 */
export function RecordingPermissionsDialog({
  open,
  onOpenChange,
  onGranted,
}: RecordingPermissionsDialogProps) {
  const [permissions, setPermissions] = useState<RecordingPermissions | null>(
    null,
  )
  const [needsRestart, setNeedsRestart] = useState(false)
  // macOS 14.4+: CoreAudio process taps replace the Screen Recording grant
  // with a normal one-click "System Audio Recording Only" prompt.
  const [usesTap, setUsesTap] = useState(false)
  const [tapStatus, setTapStatus] = useState<string>('undetermined')
  // Invalidates an in-flight system-audio request poll when the dialog closes
  // or the user clicks again.
  const tapRequestIdRef = useRef(0)

  // Note: deliberately no auto-continue when the mic becomes granted — the
  // user must still get the chance to allow (optional) system audio before
  // recording starts; Continue is the only path forward.
  const refresh = useCallback(async () => {
    const result = await checkRecordingPermissions()
    setPermissions(result)
  }, [])

  useEffect(() => {
    if (!open) return
    void refresh()
    void getRecordingSupport().then(async support => {
      const isTap = support.systemAudioMode === 'tap'
      setUsesTap(isTap)
      // Reflect the real system-audio grant on open (no prompt) so an
      // already-granted permission shows green immediately.
      if (isTap) setTapStatus(await getSystemAudioStatus())
    })
    // Re-check the real status when the user returns from System Settings.
    const onFocus = () => {
      void refresh()
      if (usesTap) void getSystemAudioStatus().then(setTapStatus)
    }
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      tapRequestIdRef.current += 1
    }
  }, [open, refresh, usesTap])

  const handleMicRequest = async () => {
    await requestMicPermission()
    await refresh()
  }

  const handleScreenRequest = async () => {
    const result = await requestScreenRecordingPermission()
    if (result === 'denied-restart-required') {
      setNeedsRestart(true)
    }
    await refresh()
  }

  /**
   * macOS won't let an app enable its own Screen Recording toggle, but
   * calling the request API first makes the system add Scrambl to the list
   * (toggled off) — so the user lands in Settings with a single switch to
   * flip instead of hunting for the app with the "+" button.
   */
  const handleOpenScreenSettings = async () => {
    const result = await requestScreenRecordingPermission().catch(() => null)
    if (result === 'denied-restart-required') {
      setNeedsRestart(true)
    }
    if (result !== 'granted') {
      await openRecordingPrivacySettings('screenRecording')
    }
    await refresh()
  }

  /**
   * One-click "System Audio Recording Only" prompt on macOS 14.4+.
   *
   * Process-tap creation triggers the prompt but returns immediately without
   * the user's answer, so instead of trusting it we fire the prompt and poll
   * the real TCC status — turning green ONLY once it actually reports
   * granted (or red on denied). Cancelled if the dialog closes.
   */
  const handleTapRequest = async () => {
    const requestId = tapRequestIdRef.current + 1
    tapRequestIdRef.current = requestId
    setTapStatus('pending')
    let openedSettings = false
    const openTapSettingsOnce = async () => {
      if (openedSettings) return
      openedSettings = true
      await Promise.resolve(openRecordingPrivacySettings('systemAudio')).catch(
        () => {},
      )
    }

    let requestResult: string | null = null
    const requestPromise = requestSystemAudioPermission()
      .then(result => {
        requestResult = result
        return result
      })
      .catch(() => {
        requestResult = 'denied'
        return 'denied'
      })

    // The backend now triggers the real macOS prompt (via the TCC SPI) and
    // blocks until the user answers, so the command result is authoritative.
    // Poll the real status alongside it so an answer reflects immediately,
    // and only fall back to System Settings once we have a definitive denial
    // (never while the prompt may still be open).
    for (let i = 0; i < 600 && tapRequestIdRef.current === requestId; i++) {
      await Promise.race([
        new Promise(resolve => setTimeout(resolve, 500)),
        requestPromise,
      ])
      if (tapRequestIdRef.current !== requestId) return

      if (requestResult === 'granted') {
        setTapStatus('granted')
        return
      }
      if (requestResult === 'denied' || requestResult === 'unsupported') {
        setTapStatus('denied')
        if (requestResult === 'denied') {
          toast.info('Enable System Audio in macOS Settings', {
            description:
              'Turn on Scrambl in Privacy & Security > System Audio Recording Only.',
          })
          await openTapSettingsOnce()
        }
        return
      }

      const status = await getSystemAudioStatus().catch(() => 'undetermined')
      if (status === 'granted' || status === 'denied') {
        setTapStatus(status)
        return
      }
    }
    if (tapRequestIdRef.current === requestId) {
      setTapStatus('denied')
      toast.info('Enable System Audio in macOS Settings', {
        description:
          'Turn on Scrambl in Privacy & Security > System Audio Recording Only.',
      })
      await openTapSettingsOnce()
    }
  }

  const handleOpenTapSettings = async () => {
    await openRecordingPrivacySettings('systemAudio')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Allow meeting recording</DialogTitle>
          <DialogDescription>
            Scrambl needs these macOS permissions to record meeting audio. The
            audio stays private and is only used to generate your notes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <PermissionRow
            icon={MicrophoneIcon}
            title="Microphone"
            description="Records your voice during meetings."
            status={permissions?.microphone ?? 'undetermined'}
            onRequest={() => void handleMicRequest()}
            onOpenSettings={() =>
              void openRecordingPrivacySettings('microphone')
            }
          />
          {usesTap ? (
            <PermissionRow
              icon={MonitorIcon}
              title="System audio"
              description="Captures the audio of other meeting participants. Your screen is never recorded. Optional: without it only your microphone is recorded."
              status={tapStatus}
              onRequest={() => void handleTapRequest()}
              onOpenSettings={() => void handleOpenTapSettings()}
            />
          ) : (
            <PermissionRow
              icon={MonitorIcon}
              title="System audio (Screen Recording)"
              description="macOS bundles system-audio capture under the Screen Recording permission — Scrambl only captures audio of other participants, never your screen. Optional: without it only your microphone is recorded."
              status={permissions?.screenRecording ?? 'undetermined'}
              onRequest={() => void handleScreenRequest()}
              onOpenSettings={() => void handleOpenScreenSettings()}
            />
          )}
        </div>

        {needsRestart && (
          <div
            className={cn(
              'flex items-center gap-2 rounded-lg border border-amber-200',
              'bg-amber-50 p-3 text-xs text-amber-800',
            )}
          >
            <WarningCircleIcon className="h-4 w-4 shrink-0" />
            After granting Screen Recording, quit and reopen Scrambl for it to
            take effect.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Not now
          </Button>
          <Button
            disabled={
              permissions?.microphone !== 'granted' ||
              (usesTap && tapStatus === 'pending')
            }
            onClick={() => onGranted?.()}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
